import { createContext, useContext } from 'react'
import { RELATIONSHIP_OPTIONS } from 'src/consumer/constants'

const ENGLISH_COPY = {
  formLanguageLabel: 'Form Language',
  formLanguageHelper: 'Choose the language that is easiest for the clinic assistant to use.',
  createPatientTitle: 'Create New Patient',
  cancelButton: 'Cancel',
  addPatientButton: 'Add Patient',
  addingPatientButton: 'Adding...',
  mobileLabel: 'Mobile',
  mobilePlaceholder: 'Mobile Number',
  mobileHelper:
    "Start with the main reachable number. We'll search existing patients from this field.",
  contactDetailsRestrictedMessage:
    'Contact details are disabled for your clinic assistant assignment.',
  anonymousPatientLabel: 'Patient has neither mobile nor email',
  anonymousPatientHelper:
    'Use this only when the patient genuinely has no reachable phone number or email address.',
  alternateMobileLabel: 'Alternate Mobile',
  alternateMobilePlaceholder: 'Alternate Mobile Number',
  alternateMobileHelper: 'Add another reachable number if the primary number is unavailable.',
  relatedPatientTitlePrefix: 'Add another patient on the same number as',
  relatedPatientTitleSuffix: '',
  relatedPatientHelper:
    'Choose how this patient is related so both patient profiles can share the number.',
  nameLabel: 'Name',
  genderLabel: 'Gender',
  ageLabel: 'Age',
  ageHelper:
    'Enter completed years first. For babies under one year, use 0 years and add months or days.',
  ageYearsLabel: 'Years',
  ageSecondaryValueLabel: 'Months / Days',
  ageSecondaryValuePlaceholder: '0',
  ageSecondaryUnitLabel: 'Unit',
  ageUnitMonths: 'Months',
  ageUnitDays: 'Days',
  emailLabel: 'Email',
  emailPlaceholder: 'Email Address',
  emailHelper: 'Use the main email if the patient can receive updates there.',
  alternateEmailLabel: 'Alternate Email',
  alternateEmailPlaceholder: 'Alternate Email Address',
  alternateEmailHelper:
    'Add other email IDs that can receive patient-related communication.',
  addressLabel: 'Address',
  addressPlaceholder: 'Address',
  addressHelper: 'Full street address or locality.',
  cityLabel: 'City',
  cityPlaceholder: 'City',
  cityHelper: 'This is shown in patient summaries across booking views.',
  pincodeLabel: 'Pincode',
  pincodePlaceholder: 'Pincode',
  patientPreferredLanguageLabel: "Patient's Preferred Language",
  patientPreferredLanguageHelper:
    'Choose the language for patient communication, if known.',
  noteLabel: 'Note',
  notePlaceholder: 'Note',
  referredByLabel: 'Referred By',
  referredByPlaceholder: 'Referred By',
  moreDetailsTitle: 'More Details (Optional)',
  attendantsLabel: 'Attendants',
  attendantNamePlaceholder: 'Attendant Name',
  attendantMobilePlaceholder: 'Mobile / WhatsApp',
  attendantEmailPlaceholder: 'Email',
  attendantLinkedAccountLabel: 'Linked account',
  communicationToAttendantLabel: 'Communication to Attendant',
  sendToPatientLabel: 'Send to Patient',
  sendToAttendantLabel: 'Send to Attendant',
  sendToOthersLabel: 'Send to Others',
  additionalRecipientsTitle: 'Additional Recipients',
  additionalRecipientsEmpty: 'No additional recipients added.',
  additionalRecipientsHint: 'Click + to add recipients.',
  recipientTitle: (index) => `Recipient ${index + 1}`,
  recipientEmailLabel: 'To Email',
  recipientEmailPlaceholder: 'recipient@example.com',
  recipientPhoneLabel: 'To Phone',
  recipientPhonePlaceholder: '+1234567890',
  recipientWhatsappLabel: 'To WhatsApp ID',
  recipientWhatsappPlaceholder: 'WhatsApp ID',
  recipientUserIdLabel: 'To User ID',
  recipientUserIdPlaceholder: 'User ID',
  recipientPatientIdLabel: 'To Patient ID',
  recipientPatientIdPlaceholder: 'Patient ID',
  recipientValidation:
    'Please provide at least one contact method (Email, Phone, or WhatsApp)',
  recipientHelper: 'Provide at least one contact method for this recipient',
  validationRequired: 'Required',
  validationChooseOne: 'Choose one',
  // ── layout / grouping ──
  moreDetailsToggleShow: 'More details',
  moreDetailsToggleHide: 'Hide details',
  moreDetailsFilledCount: (count) => `${count} filled`,
  contactGroupTitle: 'Contact',
  locationGroupTitle: 'Location',
  preferencesGroupTitle: 'Preferences',
  identifiersGroupTitle: 'Identifiers',
  adminGroupTitle: 'Communication routing (admin)',
  addAlternateMobileButton: 'Add alternate number',
  addAlternateEmailButton: 'Add alternate email',
  addAttendantButton: 'Add attendant',
  addRecipientButton: 'Add recipient',
  ageModeAgeLabel: 'Age',
  ageModeDobLabel: 'Date of birth',
  dateOfBirthLabel: 'Date of birth',
  ageFromDobPrefix: 'Age',
  noContactToggleLabel: 'No mobile or email?',
  noContactModeTitle: 'Creating without contact details',
  noContactModeBody:
    'Reminders, links and receipts cannot be sent to this patient. An internal placeholder ID is used instead of an email.',
  noContactModeUndo: 'Add contact details',
  noContactOnFileLabel: 'No contact details on file',
  addressSectionTitle: 'Address',
  attendantsSectionTitle: 'Attendants',
  otherDetailsSectionTitle: 'Other details',
  sectionCountLabel: (count) => `${count} filled`,
  stateLabel: 'State',
  countryLabel: 'Country',
  cityUseTypedLabel: (input) => `Use "${input}"`,
  cityNoResults: 'Type to search cities',
  tagsLabel: 'Tags',
  tagsPlaceholder: 'Add a tag and press Enter',
  tagsAddButton: 'Add',
  tagsHelper: 'Tags help you find and group patients later (e.g. VIP, Follow-up, Insurance).',
  tagsEmpty: 'No tags',
  importSourceLabel: 'Imported from',
  importIdLabel: 'Import ID',
  consentTitle: 'Patient consent',
  consentStatement:
    'I confirm that the patient (or their guardian) has been informed and has given explicit consent for their health information to be recorded and used for their care, for follow-ups by the clinic and its care team, and for sharing with authorised partners strictly to facilitate treatment and services.',
  consentNudgeMessage: 'Please tick the consent box above to add the patient.',
  consentRecordedTitle: 'Consent recorded',
  consentRecordedBy: (name, when, via) => `Taken by ${name || 'clinic user'} on ${when}${via ? ` (${via.replace(/-/g, ' ')})` : ''}.`,
  consentPendingChip: 'Clinical consent pending',
  consentSupportPendingTitle: 'Clinical consent pending',
  consentSupportPendingBody:
    'This patient is being created from the support portal. Consent for recording and using health data must be taken by the clinic when the patient is seen; it will show as pending until then.',
  consentFieldName: 'Consent',
  relationOfLabel: (relation, name) => `${relation} of ${name}`,
  preferredLanguageShortLabel: 'Preferred language',
  cityExactMatchLabel: 'Match',
  documentsSectionTitle: 'Documents',
  documentsUploadButton: 'Upload from computer',
  documentsTakePictureButton: 'Take picture',
  documentsEmpty: 'No documents yet. Add ID proof, old reports or referrals.',
  documentsPendingHint: 'Files will be uploaded when the patient is added.',
  documentsCategoryLabel: 'Type',
  documentsUploading: 'Uploading…',
  documentsUploadFailed: 'Upload failed',
  documentsTooLarge: 'File is larger than 25 MB',
  documentsOpen: 'Open',
  consentStripReadMore: 'Read full statement',
  consentStripReadLess: 'Hide statement',
  consentStripShort: 'Patient has been informed and consents to their health data being recorded and used for care and follow-ups.',
  attendantTitle: (index) => `Attendant ${index + 1}`,
  attendantsEmpty: 'No attendants added yet.',
  attendantRelationLabel: 'Relation',
  attendantMobileLabel: 'Mobile / WhatsApp',
  attendantEmailLabel: 'Email',
  removeLabel: 'Remove',
  ageYearsPlaceholder: 'Years',
  ageSecondaryPlaceholder: 'Months',
  ageSecondaryDaysPlaceholder: 'Days',
  patientNumberLabel: 'Patient Number',
  uhidLabel: 'UHID',
  mobileOrEmailFieldName: 'Mobile or email',
  readyToAddHint: 'All required details are in. You can add the patient.',
  missingFieldsHint: (fields) => `Still needed: ${fields}`,
  relationshipLabels: {
    relative: 'Relative',
    son: 'Son',
    daughter: 'Daughter',
    father: 'Father',
    mother: 'Mother',
    brother: 'Brother',
    sister: 'Sister',
    grandfather: 'Grandfather',
    grandmother: 'Grandmother',
    husband: 'Husband',
    wife: 'Wife',
    guardian: 'Guardian',
    friend: 'Friend',
  },
  genderLabels: {
    male: 'Male',
    female: 'Female',
    nonBinary: 'Other',
  },
  validationMessages: {
    'Name is required': 'Name is required',
    'Please choose a gender': 'Please choose a gender',
    'Valid age is required': 'Valid age is required',
    'Age is required': 'Age is required',
    'Date of birth is required': 'Date of birth is required',
    'Date of birth cannot be in the future': 'Date of birth cannot be in the future',
    'Years must be between 0 and 150': 'Years must be between 0 and 150',
    'Months must be between 0 and 11': 'Months must be between 0 and 11',
    'Days must be between 0 and 30': 'Days must be between 0 and 30',
    'Valid mobile number is required': 'Valid mobile number is required',
    'Valid email is required': 'Valid email is required',
    'Attendant name is required': 'Attendant name is required',
    'Invalid mobile number format': 'Invalid mobile number format',
    'Invalid email address': 'Invalid email address',
    'Field is required': 'Field is required',
  },
}

const HINDI_COPY = {
  formLanguageLabel: 'फॉर्म की भाषा',
  formLanguageHelper: 'जिस भाषा में काम करना आसान लगे, वही चुनें।',
  createPatientTitle: 'नया मरीज जोड़ें',
  cancelButton: 'रद्द करें',
  addPatientButton: 'मरीज जोड़ें',
  addingPatientButton: 'जोड़ा जा रहा है...',
  mobileLabel: 'मोबाइल नंबर',
  mobilePlaceholder: 'मोबाइल नंबर',
  mobileHelper:
    'सबसे पहले मरीज का मुख्य मोबाइल नंबर लिखें। इसी नंबर से पुराने रिकॉर्ड खोजे जाएंगे।',
  contactDetailsRestrictedMessage:
    'इस क्लिनिक में मरीज बनाते समय संपर्क जानकारी जोड़ने की अनुमति नहीं है।',
  anonymousPatientLabel: 'मरीज के पास न मोबाइल नंबर है, न ईमेल आईडी',
  anonymousPatientHelper:
    'इसे तभी चुनें जब मरीज के पास सच में कोई मोबाइल नंबर या ईमेल न हो।',
  alternateMobileLabel: 'दूसरा मोबाइल नंबर',
  alternateMobilePlaceholder: 'दूसरा मोबाइल नंबर',
  alternateMobileHelper:
    'अगर मुख्य नंबर पर संपर्क न हो पाए, तो दूसरा नंबर भी जोड़ दें।',
  relatedPatientTitlePrefix: 'इसी नंबर पर',
  relatedPatientTitleSuffix: 'के साथ एक और मरीज जोड़ें',
  relatedPatientHelper:
    'बताइए यह मरीज उनसे किस रिश्ते में है, ताकि दोनों प्रोफाइल एक ही नंबर से जुड़ सकें।',
  nameLabel: 'मरीज का नाम',
  genderLabel: 'लिंग',
  ageLabel: 'उम्र',
  ageHelper:
    'पहले पूरे साल लिखें। अगर बच्चा एक साल से छोटा है, तो 0 साल लिखकर महीने या दिन जोड़ दें।',
  ageYearsLabel: 'साल',
  ageSecondaryValueLabel: 'महीने / दिन',
  ageSecondaryValuePlaceholder: '0',
  ageSecondaryUnitLabel: 'इकाई',
  ageUnitMonths: 'महीने',
  ageUnitDays: 'दिन',
  emailLabel: 'ईमेल आईडी',
  emailPlaceholder: 'ईमेल आईडी',
  emailHelper: 'अगर मरीज ईमेल पर अपडेट ले सकता है, तो मुख्य ईमेल आईडी लिखें।',
  alternateEmailLabel: 'दूसरी ईमेल आईडी',
  alternateEmailPlaceholder: 'दूसरी ईमेल आईडी',
  alternateEmailHelper:
    'मरीज से जुड़ी जानकारी पाने के लिए दूसरी ईमेल आईडी भी जोड़ सकते हैं।',
  addressLabel: 'पता',
  addressPlaceholder: 'पता',
  addressHelper: 'पूरा पता या इलाके का नाम लिखें।',
  cityLabel: 'शहर',
  cityPlaceholder: 'शहर',
  cityHelper: 'यह जानकारी बुकिंग वाली स्क्रीन पर भी दिखती है।',
  pincodeLabel: 'पिन कोड',
  pincodePlaceholder: 'पिन कोड',
  patientPreferredLanguageLabel: 'मरीज की पसंदीदा भाषा',
  patientPreferredLanguageHelper:
    'अगर पता हो, तो मरीज से बात करने या मैसेज भेजने की भाषा चुनें।',
  noteLabel: 'नोट',
  notePlaceholder: 'नोट',
  referredByLabel: 'किसके रेफर करने पर आए',
  referredByPlaceholder: 'किसने भेजा',
  moreDetailsTitle: 'और जानकारी (ज़रूरी नहीं)',
  attendantsLabel: 'साथ आने वाले लोग',
  attendantNamePlaceholder: 'नाम लिखें',
  attendantMobilePlaceholder: 'मोबाइल / व्हाट्सऐप नंबर',
  attendantEmailPlaceholder: 'ईमेल आईडी',
  attendantLinkedAccountLabel: 'जुड़ा हुआ अकाउंट',
  communicationToAttendantLabel: 'अपडेट अटेंडेंट को भी भेजें',
  sendToPatientLabel: 'मरीज को भेजें',
  sendToAttendantLabel: 'अटेंडेंट को भेजें',
  sendToOthersLabel: 'दूसरों को भेजें',
  additionalRecipientsTitle: 'अतिरिक्त लोग',
  additionalRecipientsEmpty: 'अभी कोई अतिरिक्त व्यक्ति नहीं जोड़ा गया है।',
  additionalRecipientsHint: 'जोड़ने के लिए + दबाएँ।',
  recipientTitle: (index) => `व्यक्ति ${index + 1}`,
  recipientEmailLabel: 'ईमेल',
  recipientEmailPlaceholder: 'recipient@example.com',
  recipientPhoneLabel: 'फ़ोन',
  recipientPhonePlaceholder: '+1234567890',
  recipientWhatsappLabel: 'व्हाट्सऐप नंबर',
  recipientWhatsappPlaceholder: 'व्हाट्सऐप नंबर',
  recipientUserIdLabel: 'यूज़र आईडी',
  recipientUserIdPlaceholder: 'यूज़र आईडी',
  recipientPatientIdLabel: 'पेशेंट आईडी',
  recipientPatientIdPlaceholder: 'पेशेंट आईडी',
  recipientValidation:
    'कम से कम एक संपर्क तरीका दें: ईमेल, फ़ोन या व्हाट्सऐप।',
  recipientHelper: 'इस व्यक्ति के लिए कम से कम एक संपर्क जानकारी भरें।',
  validationRequired: 'ज़रूरी है',
  validationChooseOne: 'एक विकल्प चुनें',
  // ── layout / grouping ──
  moreDetailsToggleShow: 'और जानकारी',
  moreDetailsToggleHide: 'जानकारी छिपाएँ',
  moreDetailsFilledCount: (count) => `${count} भरे गए`,
  contactGroupTitle: 'संपर्क',
  locationGroupTitle: 'पता',
  preferencesGroupTitle: 'प्राथमिकताएँ',
  identifiersGroupTitle: 'पहचान संख्या',
  adminGroupTitle: 'संचार सेटिंग (एडमिन)',
  addAlternateMobileButton: 'दूसरा नंबर जोड़ें',
  addAlternateEmailButton: 'दूसरा ईमेल जोड़ें',
  addAttendantButton: 'साथ आने वाला जोड़ें',
  addRecipientButton: 'प्राप्तकर्ता जोड़ें',
  ageModeAgeLabel: 'उम्र',
  ageModeDobLabel: 'जन्म तिथि',
  dateOfBirthLabel: 'जन्म तिथि',
  ageFromDobPrefix: 'उम्र',
  noContactToggleLabel: 'मोबाइल या ईमेल नहीं है?',
  noContactModeTitle: 'बिना संपर्क जानकारी के मरीज बन रहा है',
  noContactModeBody:
    'इस मरीज को रिमाइंडर, लिंक या रसीद नहीं भेजी जा सकेंगी। ईमेल की जगह एक आंतरिक पहचान आईडी इस्तेमाल होगी।',
  noContactModeUndo: 'संपर्क जानकारी जोड़ें',
  noContactOnFileLabel: 'संपर्क जानकारी दर्ज नहीं है',
  addressSectionTitle: 'पता',
  attendantsSectionTitle: 'साथ आने वाले लोग',
  otherDetailsSectionTitle: 'अन्य जानकारी',
  sectionCountLabel: (count) => `${count} भरे गए`,
  stateLabel: 'राज्य',
  countryLabel: 'देश',
  cityUseTypedLabel: (input) => `"${input}" का उपयोग करें`,
  cityNoResults: 'शहर खोजने के लिए टाइप करें',
  tagsLabel: 'टैग',
  tagsPlaceholder: 'टैग लिखें और Enter दबाएँ',
  tagsAddButton: 'जोड़ें',
  tagsHelper: 'टैग से मरीज़ों को बाद में ढूँढना और समूह बनाना आसान होता है (जैसे VIP, फॉलो-अप, बीमा)।',
  tagsEmpty: 'कोई टैग नहीं',
  importSourceLabel: 'कहाँ से इम्पोर्ट हुआ',
  importIdLabel: 'इम्पोर्ट आईडी',
  consentTitle: 'मरीज की सहमति',
  consentStatement:
    'मैं पुष्टि करता/करती हूँ कि मरीज (या उनके अभिभावक) को समझाया गया है और उन्होंने स्पष्ट सहमति दी है कि उनकी स्वास्थ्य जानकारी उनके इलाज के लिए दर्ज और उपयोग की जाए, क्लिनिक और उसकी केयर टीम फॉलो-अप के लिए संपर्क करे, और इलाज व सेवाओं को सुगम बनाने के लिए अधिकृत साझेदारों के साथ जानकारी साझा की जाए।',
  consentNudgeMessage: 'मरीज जोड़ने के लिए कृपया ऊपर सहमति वाला बॉक्स टिक करें।',
  consentRecordedTitle: 'सहमति दर्ज है',
  consentRecordedBy: (name, when, via) => `${name || 'क्लिनिक उपयोगकर्ता'} द्वारा ${when} को ली गई${via ? ` (${via.replace(/-/g, ' ')})` : ''}।`,
  consentPendingChip: 'क्लिनिकल सहमति बाकी है',
  consentSupportPendingTitle: 'क्लिनिकल सहमति बाकी है',
  consentSupportPendingBody:
    'यह मरीज सपोर्ट पोर्टल से बनाया जा रहा है। स्वास्थ्य जानकारी दर्ज करने और उपयोग की सहमति क्लिनिक में मरीज को देखते समय ली जानी चाहिए; तब तक यह बाकी दिखेगी।',
  consentFieldName: 'सहमति',
  relationOfLabel: (relation, name) => `${name} के ${relation}`,
  preferredLanguageShortLabel: 'पसंदीदा भाषा',
  cityExactMatchLabel: 'मिला',
  documentsSectionTitle: 'दस्तावेज़',
  documentsUploadButton: 'कंप्यूटर से अपलोड करें',
  documentsTakePictureButton: 'फोटो लें',
  documentsEmpty: 'अभी कोई दस्तावेज़ नहीं। पहचान पत्र, पुरानी रिपोर्ट या रेफरल जोड़ें।',
  documentsPendingHint: 'मरीज जोड़ते ही फाइलें अपलोड हो जाएँगी।',
  documentsCategoryLabel: 'प्रकार',
  documentsUploading: 'अपलोड हो रहा है…',
  documentsUploadFailed: 'अपलोड नहीं हो सका',
  documentsTooLarge: 'फाइल 25 MB से बड़ी है',
  documentsOpen: 'खोलें',
  consentStripReadMore: 'पूरा कथन पढ़ें',
  consentStripReadLess: 'कथन छिपाएँ',
  consentStripShort: 'मरीज को समझाया गया है और वे इलाज व फॉलो-अप के लिए अपनी स्वास्थ्य जानकारी दर्ज करने और उपयोग की सहमति देते हैं।',
  attendantTitle: (index) => `साथ आने वाला ${index + 1}`,
  attendantsEmpty: 'अभी कोई साथ आने वाला नहीं जोड़ा गया।',
  attendantRelationLabel: 'रिश्ता',
  attendantMobileLabel: 'मोबाइल / व्हाट्सऐप',
  attendantEmailLabel: 'ईमेल',
  removeLabel: 'हटाएँ',
  ageYearsPlaceholder: 'साल',
  ageSecondaryPlaceholder: 'महीने',
  ageSecondaryDaysPlaceholder: 'दिन',
  patientNumberLabel: 'मरीज संख्या',
  uhidLabel: 'UHID',
  mobileOrEmailFieldName: 'मोबाइल या ईमेल',
  readyToAddHint: 'सभी ज़रूरी जानकारी भर गई है। अब मरीज जोड़ सकते हैं।',
  missingFieldsHint: (fields) => `अभी भरना बाकी: ${fields}`,
  relationshipLabels: {
    relative: 'रिश्तेदार',
    son: 'बेटा',
    daughter: 'बेटी',
    father: 'पिता',
    mother: 'माँ',
    brother: 'भाई',
    sister: 'बहन',
    grandfather: 'दादाजी',
    grandmother: 'दादी / नानी',
    husband: 'पति',
    wife: 'पत्नी',
    guardian: 'अभिभावक',
    friend: 'मित्र',
  },
  genderLabels: {
    male: 'पुरुष',
    female: 'महिला',
    nonBinary: 'अन्य',
  },
  validationMessages: {
    'Name is required': 'मरीज का नाम ज़रूरी है',
    'Please choose a gender': 'कृपया लिंग चुनें',
    'Valid age is required': 'उम्र सही भरें',
    'Age is required': 'उम्र भरना ज़रूरी है',
    'Date of birth is required': 'जन्म तिथि ज़रूरी है',
    'Date of birth cannot be in the future': 'जन्म तिथि आने वाली तारीख नहीं हो सकती',
    'Years must be between 0 and 150': 'साल 0 से 150 के बीच होने चाहिए',
    'Months must be between 0 and 11': 'महीने 0 से 11 के बीच भरें',
    'Days must be between 0 and 30': 'दिन 0 से 30 के बीच भरें',
    'Valid mobile number is required': 'सही मोबाइल नंबर ज़रूरी है',
    'Valid email is required': 'सही ईमेल आईडी ज़रूरी है',
    'Attendant name is required': 'नाम ज़रूरी है',
    'Invalid mobile number format': 'मोबाइल नंबर सही नहीं है',
    'Invalid email address': 'ईमेल आईडी सही नहीं है',
    'Field is required': 'यह जानकारी ज़रूरी है',
  },
}

const KANNADA_COPY = {
  formLanguageLabel: 'ಫಾರ್ಮ್ ಭಾಷೆ',
  formLanguageHelper: 'ಕ್ಲಿನಿಕ್ ಅಸಿಸ್ಟೆಂಟ್‌ಗೆ ಯಾವ ಭಾಷೆ ಸುಲಭವಾಗುತ್ತದೋ ಅದನ್ನೇ ಆಯ್ಕೆ ಮಾಡಿ.',
  createPatientTitle: 'ಹೊಸ ರೋಗಿಯನ್ನು ಸೇರಿಸಿ',
  cancelButton: 'ರದ್ದು ಮಾಡಿ',
  addPatientButton: 'ರೋಗಿಯನ್ನು ಸೇರಿಸಿ',
  addingPatientButton: 'ಸೇರಿಸಲಾಗುತ್ತಿದೆ...',
  mobileLabel: 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  mobilePlaceholder: 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  mobileHelper:
    'ಮೊದಲು ರೋಗಿಯ ಮುಖ್ಯವಾಗಿ ಸಂಪರ್ಕಿಸಬಹುದಾದ ಸಂಖ್ಯೆಯನ್ನು ಬರೆಯಿರಿ. ಇದೇ ಸಂಖ್ಯೆಯಿಂದ ಹಳೆಯ ದಾಖಲೆಗಳನ್ನು ಹುಡುಕಲಾಗುತ್ತದೆ.',
  contactDetailsRestrictedMessage:
    'ಈ ಕ್ಲಿನಿಕ್‌ನಲ್ಲಿ ರೋಗಿಯನ್ನು ರಚಿಸುವಾಗ ಸಂಪರ್ಕ ವಿವರಗಳನ್ನು ಸೇರಿಸಲು ಅನುಮತಿ ಇಲ್ಲ.',
  anonymousPatientLabel: 'ರೋಗಿಯ ಬಳಿ ಮೊಬೈಲ್ ಸಂಖ್ಯೆಯೂ ಇಲ್ಲ, ಇಮೇಲ್ ಕೂಡ ಇಲ್ಲ',
  anonymousPatientHelper:
    'ರೋಗಿಯ ಬಳಿ ನಿಜವಾಗಿಯೂ ಯಾವುದೇ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಅಥವಾ ಇಮೇಲ್ ಇಲ್ಲದಿದ್ದಾಗ ಮಾತ್ರ ಇದನ್ನು ಆಯ್ಕೆ ಮಾಡಿ.',
  alternateMobileLabel: 'ಇನ್ನೊಂದು ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  alternateMobilePlaceholder: 'ಇನ್ನೊಂದು ಮೊಬೈಲ್ ಸಂಖ್ಯೆ',
  alternateMobileHelper:
    'ಮುಖ್ಯ ಸಂಖ್ಯೆಗೆ ಸಂಪರ್ಕ ಸಿಗದಿದ್ದರೆ ಉಪಯೋಗವಾಗುವ ಇನ್ನೊಂದು ಸಂಖ್ಯೆಯನ್ನು ಕೂಡ ಸೇರಿಸಿ.',
  relatedPatientTitlePrefix: 'ಈ ಸಂಖ್ಯೆಯಲ್ಲಿ',
  relatedPatientTitleSuffix: 'ಅವರಿಗೆ ಸಂಬಂಧಿಸಿದ ಮತ್ತೊಬ್ಬ ರೋಗಿಯನ್ನು ಸೇರಿಸಿ',
  relatedPatientHelper:
    'ಈ ರೋಗಿ ಅವರಿಗೆ ಯಾವ ಸಂಬಂಧ ಎಂದು ಆಯ್ಕೆ ಮಾಡಿ, ಹೀಗಾಗಿ ಎರಡೂ ಪ್ರೊಫೈಲ್‌ಗಳು ಒಂದೇ ಸಂಖ್ಯೆಯನ್ನು ಹಂಚಿಕೊಳ್ಳಬಹುದು.',
  nameLabel: 'ರೋಗಿಯ ಹೆಸರು',
  genderLabel: 'ಲಿಂಗ',
  ageLabel: 'ವಯಸ್ಸು',
  ageHelper:
    'ಮೊದಲು ಪೂರ್ಣ ವರ್ಷಗಳನ್ನು ಬರೆಯಿರಿ. ಮಗು ಒಂದು ವರ್ಷದೊಳಗಿದ್ದರೆ 0 ವರ್ಷ ಬರೆದು ತಿಂಗಳುಗಳು ಅಥವಾ ದಿನಗಳನ್ನು ಸೇರಿಸಿ.',
  ageYearsLabel: 'ವರ್ಷ',
  ageSecondaryValueLabel: 'ತಿಂಗಳು / ದಿನ',
  ageSecondaryValuePlaceholder: '0',
  ageSecondaryUnitLabel: 'ಅಳತೆ',
  ageUnitMonths: 'ತಿಂಗಳು',
  ageUnitDays: 'ದಿನ',
  emailLabel: 'ಇಮೇಲ್ ಐಡಿ',
  emailPlaceholder: 'ಇಮೇಲ್ ಐಡಿ',
  emailHelper: 'ರೋಗಿಗೆ ಇಮೇಲ್‌ನಲ್ಲಿ ಅಪ್‌ಡೇಟ್‌ಗಳು ಬರಬಹುದಾದರೆ, ಮುಖ್ಯ ಇಮೇಲ್ ಐಡಿಯನ್ನು ಬರೆಯಿರಿ.',
  alternateEmailLabel: 'ಇನ್ನೊಂದು ಇಮೇಲ್ ಐಡಿ',
  alternateEmailPlaceholder: 'ಇನ್ನೊಂದು ಇಮೇಲ್ ಐಡಿ',
  alternateEmailHelper:
    'ರೋಗಿಗೆ ಸಂಬಂಧಿಸಿದ ಮಾಹಿತಿ ಹೋಗಲು ಇನ್ನೊಂದು ಇಮೇಲ್ ಐಡಿಯನ್ನು ಕೂಡ ಸೇರಿಸಬಹುದು.',
  addressLabel: 'ವಿಳಾಸ',
  addressPlaceholder: 'ವಿಳಾಸ',
  addressHelper: 'ಪೂರ್ಣ ವಿಳಾಸ ಅಥವಾ ಪ್ರದೇಶದ ಹೆಸರನ್ನು ಬರೆಯಿರಿ.',
  cityLabel: 'ನಗರ',
  cityPlaceholder: 'ನಗರ',
  cityHelper: 'ಈ ಮಾಹಿತಿ ಬುಕ್ಕಿಂಗ್ ಸ್ಕ್ರೀನ್‌ನಲ್ಲೂ ಕಾಣಿಸುತ್ತದೆ.',
  pincodeLabel: 'ಪಿನ್ ಕೋಡ್',
  pincodePlaceholder: 'ಪಿನ್ ಕೋಡ್',
  patientPreferredLanguageLabel: 'ರೋಗಿಯ ಇಷ್ಟದ ಭಾಷೆ',
  patientPreferredLanguageHelper:
    'ಗೊತ್ತಿದ್ದರೆ, ರೋಗಿಗೆ ಮಾತನಾಡಲು ಅಥವಾ ಸಂದೇಶ ಕಳುಹಿಸಲು ಬಳಸುವ ಭಾಷೆಯನ್ನು ಆಯ್ಕೆ ಮಾಡಿ.',
  noteLabel: 'ಟಿಪ್ಪಣಿ',
  notePlaceholder: 'ಟಿಪ್ಪಣಿ',
  referredByLabel: 'ಯಾರು ರೆಫರ್ ಮಾಡಿದ್ದಾರೆ',
  referredByPlaceholder: 'ಯಾರು ಕಳುಹಿಸಿದ್ದಾರೆ',
  moreDetailsTitle: 'ಇನ್ನಷ್ಟು ಮಾಹಿತಿ (ಐಚ್ಛಿಕ)',
  attendantsLabel: 'ಜೊತೆಗೆ ಬರುವವರು',
  attendantNamePlaceholder: 'ಹೆಸರು ಬರೆಯಿರಿ',
  attendantMobilePlaceholder: 'ಮೊಬೈಲ್ / ವಾಟ್ಸ್ಆಪ್ ಸಂಖ್ಯೆ',
  attendantEmailPlaceholder: 'ಇಮೇಲ್ ಐಡಿ',
  attendantLinkedAccountLabel: 'ಲಿಂಕ್ ಆದ ಖಾತೆ',
  communicationToAttendantLabel: 'ಅಪ್‌ಡೇಟ್‌ಗಳನ್ನು ಅಟೆಂಡೆಂಟ್‌ಗೂ ಕಳುಹಿಸಿ',
  sendToPatientLabel: 'ರೋಗಿಗೆ ಕಳುಹಿಸಿ',
  sendToAttendantLabel: 'ಅಟೆಂಡೆಂಟ್‌ಗೆ ಕಳುಹಿಸಿ',
  sendToOthersLabel: 'ಇತರರಿಗೆ ಕಳುಹಿಸಿ',
  additionalRecipientsTitle: 'ಹೆಚ್ಚುವರಿ ಜನ',
  additionalRecipientsEmpty: 'ಈಗಾಗಲೇ ಯಾವುದೇ ಹೆಚ್ಚುವರಿ ವ್ಯಕ್ತಿಯನ್ನು ಸೇರಿಸಲಾಗಿಲ್ಲ.',
  additionalRecipientsHint: 'ಸೇರಿಸಲು + ಒತ್ತಿರಿ.',
  recipientTitle: (index) => `ವ್ಯಕ್ತಿ ${index + 1}`,
  recipientEmailLabel: 'ಇಮೇಲ್',
  recipientEmailPlaceholder: 'recipient@example.com',
  recipientPhoneLabel: 'ಫೋನ್',
  recipientPhonePlaceholder: '+1234567890',
  recipientWhatsappLabel: 'ವಾಟ್ಸ್ಆಪ್ ಸಂಖ್ಯೆ',
  recipientWhatsappPlaceholder: 'ವಾಟ್ಸ್ಆಪ್ ಸಂಖ್ಯೆ',
  recipientUserIdLabel: 'ಯೂಸರ್ ಐಡಿ',
  recipientUserIdPlaceholder: 'ಯೂಸರ್ ಐಡಿ',
  recipientPatientIdLabel: 'ಪೇಷೆಂಟ್ ಐಡಿ',
  recipientPatientIdPlaceholder: 'ಪೇಷೆಂಟ್ ಐಡಿ',
  recipientValidation:
    'ಕನಿಷ್ಠ ಒಂದು ಸಂಪರ್ಕ ಮಾಹಿತಿ ನೀಡಿ: ಇಮೇಲ್, ಫೋನ್ ಅಥವಾ ವಾಟ್ಸ್ಆಪ್.',
  recipientHelper: 'ಈ ವ್ಯಕ್ತಿಗಾಗಿ ಕನಿಷ್ಠ ಒಂದು ಸಂಪರ್ಕ ಮಾಹಿತಿ ನೀಡಿ.',
  validationRequired: 'ಇದು ಅಗತ್ಯ',
  validationChooseOne: 'ಒಂದು ಆಯ್ಕೆ ಮಾಡಿ',
  // ── layout / grouping ──
  moreDetailsToggleShow: 'ಇನ್ನಷ್ಟು ಮಾಹಿತಿ',
  moreDetailsToggleHide: 'ಮಾಹಿತಿ ಮರೆಮಾಡಿ',
  moreDetailsFilledCount: (count) => `${count} ಭರ್ತಿಯಾಗಿದೆ`,
  contactGroupTitle: 'ಸಂಪರ್ಕ',
  locationGroupTitle: 'ವಿಳಾಸ',
  preferencesGroupTitle: 'ಆದ್ಯತೆಗಳು',
  identifiersGroupTitle: 'ಗುರುತಿನ ಸಂಖ್ಯೆಗಳು',
  adminGroupTitle: 'ಸಂವಹನ ಸೆಟ್ಟಿಂಗ್ (ಅಡ್ಮಿನ್)',
  addAlternateMobileButton: 'ಬೇರೆ ಸಂಖ್ಯೆ ಸೇರಿಸಿ',
  addAlternateEmailButton: 'ಬೇರೆ ಇಮೇಲ್ ಸೇರಿಸಿ',
  addAttendantButton: 'ಜೊತೆಗೆ ಬರುವವರನ್ನು ಸೇರಿಸಿ',
  addRecipientButton: 'ಸ್ವೀಕರಿಸುವವರನ್ನು ಸೇರಿಸಿ',
  ageModeAgeLabel: 'ವಯಸ್ಸು',
  ageModeDobLabel: 'ಜನ್ಮ ದಿನಾಂಕ',
  dateOfBirthLabel: 'ಜನ್ಮ ದಿನಾಂಕ',
  ageFromDobPrefix: 'ವಯಸ್ಸು',
  noContactToggleLabel: 'ಮೊಬೈಲ್ ಅಥವಾ ಇಮೇಲ್ ಇಲ್ಲವೇ?',
  noContactModeTitle: 'ಸಂಪರ್ಕ ವಿವರಗಳಿಲ್ಲದೆ ರೋಗಿಯನ್ನು ಸೇರಿಸಲಾಗುತ್ತಿದೆ',
  noContactModeBody:
    'ಈ ರೋಗಿಗೆ ರಿಮೈಂಡರ್, ಲಿಂಕ್ ಅಥವಾ ರಸೀದಿ ಕಳುಹಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ. ಇಮೇಲ್ ಬದಲಿಗೆ ಆಂತರಿಕ ಗುರುತಿನ ಐಡಿ ಬಳಸಲಾಗುತ್ತದೆ.',
  noContactModeUndo: 'ಸಂಪರ್ಕ ವಿವರ ಸೇರಿಸಿ',
  noContactOnFileLabel: 'ಸಂಪರ್ಕ ವಿವರಗಳು ದಾಖಲಾಗಿಲ್ಲ',
  addressSectionTitle: 'ವಿಳಾಸ',
  attendantsSectionTitle: 'ಜೊತೆಗೆ ಬರುವವರು',
  otherDetailsSectionTitle: 'ಇತರ ವಿವರಗಳು',
  sectionCountLabel: (count) => `${count} ಭರ್ತಿಯಾಗಿದೆ`,
  stateLabel: 'ರಾಜ್ಯ',
  countryLabel: 'ದೇಶ',
  cityUseTypedLabel: (input) => `"${input}" ಬಳಸಿ`,
  cityNoResults: 'ನಗರ ಹುಡುಕಲು ಟೈಪ್ ಮಾಡಿ',
  tagsLabel: 'ಟ್ಯಾಗ್‌ಗಳು',
  tagsPlaceholder: 'ಟ್ಯಾಗ್ ಬರೆದು Enter ಒತ್ತಿ',
  tagsAddButton: 'ಸೇರಿಸಿ',
  tagsHelper: 'ಟ್ಯಾಗ್‌ಗಳಿಂದ ರೋಗಿಗಳನ್ನು ನಂತರ ಹುಡುಕುವುದು ಮತ್ತು ಗುಂಪು ಮಾಡುವುದು ಸುಲಭ (ಉದಾ. VIP, ಫಾಲೋ-ಅಪ್, ವಿಮೆ).',
  tagsEmpty: 'ಟ್ಯಾಗ್‌ಗಳಿಲ್ಲ',
  importSourceLabel: 'ಎಲ್ಲಿಂದ ಇಂಪೋರ್ಟ್ ಆಗಿದೆ',
  importIdLabel: 'ಇಂಪೋರ್ಟ್ ಐಡಿ',
  consentTitle: 'ರೋಗಿಯ ಸಮ್ಮತಿ',
  consentStatement:
    'ರೋಗಿಗೆ (ಅಥವಾ ಅವರ ಪೋಷಕರಿಗೆ) ವಿವರಿಸಲಾಗಿದೆ ಮತ್ತು ಅವರ ಆರೋಗ್ಯ ಮಾಹಿತಿಯನ್ನು ಚಿಕಿತ್ಸೆಗಾಗಿ ದಾಖಲಿಸಿ ಬಳಸಲು, ಕ್ಲಿನಿಕ್ ಮತ್ತು ಅದರ ಕೇರ್ ತಂಡ ಫಾಲೋ-ಅಪ್‌ಗಾಗಿ ಸಂಪರ್ಕಿಸಲು, ಹಾಗೂ ಚಿಕಿತ್ಸೆ ಮತ್ತು ಸೇವೆಗಳನ್ನು ಸುಗಮಗೊಳಿಸಲು ಅಧಿಕೃತ ಪಾಲುದಾರರೊಂದಿಗೆ ಹಂಚಿಕೊಳ್ಳಲು ಸ್ಪಷ್ಟ ಸಮ್ಮತಿ ನೀಡಿದ್ದಾರೆ ಎಂದು ನಾನು ದೃಢೀಕರಿಸುತ್ತೇನೆ.',
  consentNudgeMessage: 'ರೋಗಿಯನ್ನು ಸೇರಿಸಲು ದಯವಿಟ್ಟು ಮೇಲಿನ ಸಮ್ಮತಿ ಬಾಕ್ಸ್ ಅನ್ನು ಟಿಕ್ ಮಾಡಿ.',
  consentRecordedTitle: 'ಸಮ್ಮತಿ ದಾಖಲಾಗಿದೆ',
  consentRecordedBy: (name, when, via) => `${name || 'ಕ್ಲಿನಿಕ್ ಬಳಕೆದಾರ'} ಅವರು ${when} ರಂದು ಪಡೆದಿದ್ದಾರೆ${via ? ` (${via.replace(/-/g, ' ')})` : ''}.`,
  consentPendingChip: 'ಕ್ಲಿನಿಕಲ್ ಸಮ್ಮತಿ ಬಾಕಿ ಇದೆ',
  consentSupportPendingTitle: 'ಕ್ಲಿನಿಕಲ್ ಸಮ್ಮತಿ ಬಾಕಿ ಇದೆ',
  consentSupportPendingBody:
    'ಈ ರೋಗಿಯನ್ನು ಸಪೋರ್ಟ್ ಪೋರ್ಟಲ್‌ನಿಂದ ಸೇರಿಸಲಾಗುತ್ತಿದೆ. ಆರೋಗ್ಯ ಮಾಹಿತಿ ದಾಖಲಿಸಲು ಮತ್ತು ಬಳಸಲು ಸಮ್ಮತಿಯನ್ನು ರೋಗಿಯನ್ನು ನೋಡುವಾಗ ಕ್ಲಿನಿಕ್ ಪಡೆಯಬೇಕು; ಅಲ್ಲಿಯವರೆಗೆ ಇದು ಬಾಕಿ ಎಂದು ತೋರಿಸುತ್ತದೆ.',
  consentFieldName: 'ಸಮ್ಮತಿ',
  relationOfLabel: (relation, name) => `${name} ಅವರ ${relation}`,
  preferredLanguageShortLabel: 'ಆದ್ಯತೆಯ ಭಾಷೆ',
  cityExactMatchLabel: 'ಹೊಂದಿಕೆ',
  documentsSectionTitle: 'ದಾಖಲೆಗಳು',
  documentsUploadButton: 'ಕಂಪ್ಯೂಟರ್‌ನಿಂದ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ',
  documentsTakePictureButton: 'ಫೋಟೋ ತೆಗೆಯಿರಿ',
  documentsEmpty: 'ಇನ್ನೂ ದಾಖಲೆಗಳಿಲ್ಲ. ಗುರುತಿನ ಚೀಟಿ, ಹಳೆಯ ವರದಿಗಳು ಅಥವಾ ರೆಫರಲ್ ಸೇರಿಸಿ.',
  documentsPendingHint: 'ರೋಗಿಯನ್ನು ಸೇರಿಸಿದಾಗ ಫೈಲ್‌ಗಳು ಅಪ್‌ಲೋಡ್ ಆಗುತ್ತವೆ.',
  documentsCategoryLabel: 'ಪ್ರಕಾರ',
  documentsUploading: 'ಅಪ್‌ಲೋಡ್ ಆಗುತ್ತಿದೆ…',
  documentsUploadFailed: 'ಅಪ್‌ಲೋಡ್ ವಿಫಲವಾಗಿದೆ',
  documentsTooLarge: 'ಫೈಲ್ 25 MB ಗಿಂತ ದೊಡ್ಡದಾಗಿದೆ',
  documentsOpen: 'ತೆರೆಯಿರಿ',
  consentStripReadMore: 'ಪೂರ್ಣ ಹೇಳಿಕೆ ಓದಿ',
  consentStripReadLess: 'ಹೇಳಿಕೆ ಮರೆಮಾಡಿ',
  consentStripShort: 'ರೋಗಿಗೆ ವಿವರಿಸಲಾಗಿದೆ ಮತ್ತು ಚಿಕಿತ್ಸೆ ಹಾಗೂ ಫಾಲೋ-ಅಪ್‌ಗಾಗಿ ತಮ್ಮ ಆರೋಗ್ಯ ಮಾಹಿತಿ ದಾಖಲಿಸಿ ಬಳಸಲು ಅವರು ಸಮ್ಮತಿಸಿದ್ದಾರೆ.',
  attendantTitle: (index) => `ಜೊತೆಗೆ ಬರುವವರು ${index + 1}`,
  attendantsEmpty: 'ಇನ್ನೂ ಯಾರನ್ನೂ ಸೇರಿಸಿಲ್ಲ.',
  attendantRelationLabel: 'ಸಂಬಂಧ',
  attendantMobileLabel: 'ಮೊಬೈಲ್ / ವಾಟ್ಸ್ಆಪ್',
  attendantEmailLabel: 'ಇಮೇಲ್',
  removeLabel: 'ತೆಗೆದುಹಾಕಿ',
  ageYearsPlaceholder: 'ವರ್ಷಗಳು',
  ageSecondaryPlaceholder: 'ತಿಂಗಳುಗಳು',
  ageSecondaryDaysPlaceholder: 'ದಿನಗಳು',
  patientNumberLabel: 'ರೋಗಿ ಸಂಖ್ಯೆ',
  uhidLabel: 'UHID',
  mobileOrEmailFieldName: 'ಮೊಬೈಲ್ ಅಥವಾ ಇಮೇಲ್',
  readyToAddHint: 'ಎಲ್ಲಾ ಅಗತ್ಯ ಮಾಹಿತಿ ಭರ್ತಿಯಾಗಿದೆ. ಈಗ ರೋಗಿಯನ್ನು ಸೇರಿಸಬಹುದು.',
  missingFieldsHint: (fields) => `ಇನ್ನೂ ಬೇಕಾಗಿದೆ: ${fields}`,
  relationshipLabels: {
    relative: 'ಬಂಧು',
    son: 'ಮಗ',
    daughter: 'ಮಗಳು',
    father: 'ತಂದೆ',
    mother: 'ತಾಯಿ',
    brother: 'ಅಣ್ಣ / ತಮ್ಮ',
    sister: 'ಅಕ್ಕ / ತಂಗಿ',
    grandfather: 'ತಾತ',
    grandmother: 'ಅಜ್ಜಿ',
    husband: 'ಗಂಡ',
    wife: 'ಹೆಂಡತಿ',
    guardian: 'ಅಭಿಭಾವಕ',
    friend: 'ಸ್ನೇಹಿತ',
  },
  genderLabels: {
    male: 'ಪುರುಷ',
    female: 'ಮಹಿಳೆ',
    nonBinary: 'ಇತರೆ',
  },
  validationMessages: {
    'Name is required': 'ರೋಗಿಯ ಹೆಸರು ಅಗತ್ಯ',
    'Please choose a gender': 'ದಯವಿಟ್ಟು ಲಿಂಗವನ್ನು ಆಯ್ಕೆ ಮಾಡಿ',
    'Valid age is required': 'ಸರಿಯಾದ ವಯಸ್ಸನ್ನು ನೀಡಿ',
    'Age is required': 'ವಯಸ್ಸು ತುಂಬುವುದು ಅಗತ್ಯ',
    'Date of birth is required': 'ಜನ್ಮ ದಿನಾಂಕ ಅಗತ್ಯ',
    'Date of birth cannot be in the future': 'ಜನ್ಮ ದಿನಾಂಕ ಮುಂದಿನ ದಿನಾಂಕವಾಗಿರಬಾರದು',
    'Years must be between 0 and 150': 'ವರ್ಷಗಳು 0 ರಿಂದ 150ರ ನಡುವೆ ಇರಬೇಕು',
    'Months must be between 0 and 11': 'ತಿಂಗಳುಗಳು 0 ರಿಂದ 11ರ ನಡುವೆ ಇರಬೇಕು',
    'Days must be between 0 and 30': 'ದಿನಗಳು 0 ರಿಂದ 30ರ ನಡುವೆ ಇರಬೇಕು',
    'Valid mobile number is required': 'ಸರಿಯಾದ ಮೊಬೈಲ್ ಸಂಖ್ಯೆ ಅಗತ್ಯ',
    'Valid email is required': 'ಸರಿಯಾದ ಇಮೇಲ್ ಐಡಿ ಅಗತ್ಯ',
    'Attendant name is required': 'ಹೆಸರು ಅಗತ್ಯ',
    'Invalid mobile number format': 'ಮೊಬೈಲ್ ಸಂಖ್ಯೆಯ ರೂಪ ಸರಿಯಿಲ್ಲ',
    'Invalid email address': 'ಇಮೇಲ್ ಐಡಿ ಸರಿಯಿಲ್ಲ',
    'Field is required': 'ಈ ಮಾಹಿತಿ ಅಗತ್ಯ',
  },
}

const LANGUAGE_LABELS = {
  en: {
    id: 'en',
    language: 'English',
    localLanguageName: 'English',
  },
  hi: {
    id: 'hi',
    language: 'Hindi',
    localLanguageName: 'हिंदी',
  },
  kn: {
    id: 'kn',
    language: 'Kannada',
    localLanguageName: 'ಕನ್ನಡ',
  },
}

const COPY_BY_LANGUAGE = {
  en: ENGLISH_COPY,
  hi: HINDI_COPY,
  kn: KANNADA_COPY,
}

export const PatientFormI18nContext = createContext({
  uiLanguageCode: 'en',
  copy: ENGLISH_COPY,
  genderOptions: [],
  relationshipOptions: RELATIONSHIP_OPTIONS,
  uiLanguageOptions: Object.values(LANGUAGE_LABELS),
  translateValidationMessage: (message) => message,
})

export function usePatientFormI18n() {
  return useContext(PatientFormI18nContext)
}

export function normalizePatientFormUiLanguage(languageCode) {
  if (!languageCode || typeof languageCode !== 'string') {
    return 'en'
  }

  const normalizedLanguage = languageCode.trim().toLowerCase().split('-')[0]
  return COPY_BY_LANGUAGE[normalizedLanguage] ? normalizedLanguage : 'en'
}

export function getSupportedPatientFormUiLanguage(languageCode) {
  return normalizePatientFormUiLanguage(languageCode)
}

// The clinic assistant's chosen form-fill language is remembered on the device so it persists across
// bookings (and page reloads) instead of resetting to the clinic/profile default each time.
export const PATIENT_FORM_UI_LANGUAGE_STORAGE_KEY = 'patientFormUiLanguagePreference'

export function readStoredPatientFormUiLanguage() {
  if (typeof window === 'undefined') {
    return ''
  }
  try {
    return window.localStorage.getItem(PATIENT_FORM_UI_LANGUAGE_STORAGE_KEY) || ''
  } catch (error) {
    return ''
  }
}

export function persistPatientFormUiLanguage(languageCode) {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(
      PATIENT_FORM_UI_LANGUAGE_STORAGE_KEY,
      normalizePatientFormUiLanguage(languageCode),
    )
  } catch (error) {
    // Ignore storage failures (private mode / disabled storage) — language still applies in-session.
  }
}

function getUiLanguageOptions(appLanguages = []) {
  const availableSupportedLanguages = Array.isArray(appLanguages)
    ? appLanguages
        .map((language) => normalizePatientFormUiLanguage(language?.id))
        .filter((languageCode, index, list) => Boolean(languageCode) && list.indexOf(languageCode) === index)
    : []

  const languageCodes = Array.from(
    new Set([...(availableSupportedLanguages || []), 'en', 'hi', 'kn']),
  )
  return languageCodes.map((languageCode) => LANGUAGE_LABELS[languageCode]).filter(Boolean)
}

function getGenderOptions(copy) {
  return [
    { id: 'male', label: copy.genderLabels.male },
    { id: 'female', label: copy.genderLabels.female },
    { id: 'nonBinary', label: copy.genderLabels.nonBinary },
  ]
}

function getRelationshipOptions(copy) {
  return RELATIONSHIP_OPTIONS.map((option) => ({
    ...option,
    label: copy.relationshipLabels[option.value] || option.label,
  }))
}

export function buildPatientFormI18n(languageCode, appLanguages = []) {
  const uiLanguageCode = normalizePatientFormUiLanguage(languageCode)
  const copy = COPY_BY_LANGUAGE[uiLanguageCode] || ENGLISH_COPY

  return {
    uiLanguageCode,
    copy,
    genderOptions: getGenderOptions(copy),
    relationshipOptions: getRelationshipOptions(copy),
    uiLanguageOptions: getUiLanguageOptions(appLanguages),
    translateValidationMessage: (message) =>
      copy.validationMessages[message] || ENGLISH_COPY.validationMessages[message] || message,
  }
}
